/**
 * Маппинг реальных статусов креативов в упрощенные для отображения
 * @param {string|null|undefined} status - Реальный статус из БД
 * @param {boolean} isAdmin - Является ли пользователь админом
 * @returns {string} Статус для отображения
 */
export function mapCreativeStatus(status, isAdmin = false) {
    if (!status) {
        return 'moderation';
    }

    const statusLower = String(status).toLowerCase().trim();

    if (isAdmin) {
        return statusLower;
    }

    if (statusLower === 'pending_approval') {
        return 'moderation';
    }

    return statusLower;
}

/** @type {Record<string, string>} */
const RTB_CREATIVE_STATUS_LABELS = {
    pending_approval: 'На модерации',
    approve: 'На модерации',
    review: 'На модерации',
    moderation: 'На модерации',
    sent_to_ssp: 'На стороне SSP',
    confirmed: 'Подтверждён',
    rejected: 'Отклонён',
    cancelled: 'Отменён',
    cancel: 'Отменён',
    active: 'Активен',
};

/** @type {Record<string, string>} badge suffix for status-badge--* */
const RTB_CREATIVE_STATUS_BADGE = {
    pending_approval: 'pending',
    approve: 'pending',
    review: 'pending',
    moderation: 'pending',
    sent_to_ssp: 'pending',
    confirmed: 'active',
    active: 'active',
    rejected: 'rejected',
    cancelled: 'paused',
    cancel: 'paused',
};

/**
 * Получить label для статуса креатива
 * @param {string} status - Статус для отображения
 * @returns {string} Текстовый label
 */
export function getCreativeStatusLabel(status) {
    if (!status) {
        return RTB_CREATIVE_STATUS_LABELS.moderation;
    }

    const key = String(status).toLowerCase().trim();
    return RTB_CREATIVE_STATUS_LABELS[key] || status;
}

/**
 * CSS-класс badge для RTB-креатива (status-badge--pending и т.д.)
 * @param {string|null|undefined} status
 * @returns {string}
 */
export function getCreativeStatusBadgeClass(status) {
    if (!status) {
        return 'pending';
    }

    const key = String(status).toLowerCase().trim();
    return RTB_CREATIVE_STATUS_BADGE[key] || 'pending';
}

/**
 * Получить display status и label для креатива
 * @param {string|null|undefined} status - Реальный статус из БД
 * @param {boolean} isAdmin - Является ли пользователь админом
 * @returns {{displayStatus: string, displayLabel: string, badgeClass: string}}
 */
export function getCreativeDisplayStatus(status, isAdmin = false) {
    const raw = status ? String(status).toLowerCase().trim() : 'pending_approval';
    const displayLabel = getCreativeStatusLabel(raw);
    const badgeClass = getCreativeStatusBadgeClass(raw);

    return {
        displayStatus: mapCreativeStatus(status, isAdmin),
        displayLabel,
        badgeClass,
    };
}

/** @type {Record<string, string>} */
const READINESS_COVERAGE_LABELS = {
    none: 'Не готово',
    partial: 'Частично',
    full: 'Готово',
};

/**
 * @param {{ coverage?: string, ready_target_count?: number, required_target_count?: number }|null|undefined} details
 * @returns {string}
 */
export function getReadinessCoverageLabel(details) {
    if (!details || !details.required_target_count) {
        return '';
    }

    const coverage = String(details.coverage || 'none').toLowerCase();
    const base = READINESS_COVERAGE_LABELS[coverage] || coverage;
    const ready = Number(details.ready_target_count ?? 0);
    const total = Number(details.required_target_count ?? 0);

    return `${base} (${ready}/${total})`;
}

/**
 * @param {{ missing_targets?: Array<{ label?: string, locales?: Array<{ locale?: string, status?: string|null }> }> }|null|undefined} details
 * @returns {string}
 */
export function buildMissingTargetsTooltip(details) {
    const targets = details?.missing_targets;
    if (!Array.isArray(targets) || targets.length === 0) {
        return '';
    }

    return targets
        .map((target) => {
            const label = target.label || 'Target';
            const locales = (target.locales || [])
                .map((entry) => `${entry.locale}: ${entry.status || 'не отправлено'}`)
                .join(', ');

            return locales ? `${label} (${locales})` : label;
        })
        .join('\n');
}

/**
 * @param {object|null|undefined} creative
 * @param {{ ready?: boolean, readiness_details?: object }|null|undefined} override
 * @returns {boolean}
 */
export function isCreativeReadyForImpressions(creative, override = null) {
    if (override && typeof override.ready === 'boolean') {
        return override.ready;
    }

    if (typeof creative?.ready_for_impressions === 'boolean') {
        return creative.ready_for_impressions;
    }

    return false;
}

/**
 * Admin-facing status line: workflow status + optional required coverage.
 * @param {string|null|undefined} status
 * @param {{ coverage?: string, ready_target_count?: number, required_target_count?: number }|null|undefined} readinessDetails
 * @returns {string}
 */
export function getAdminCreativeStatusWithCoverage(status, readinessDetails) {
    const statusLabel = getCreativeStatusLabel(status);
    const coverageLabel = getReadinessCoverageLabel(readinessDetails);

    if (!coverageLabel) {
        return statusLabel;
    }

    return `${statusLabel} · ${coverageLabel} required`;
}
